import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Globe, Mail, Phone, MapPin, ExternalLink, CheckCircle2, XCircle, Clock, ArrowUpRight, Image as ImageIcon, Home, Tag, Zap, Percent, Hash } from 'lucide-react';

const STATUS_META = {
  new:      { label: 'New',      cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  reviewed: { label: 'Reviewed', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  promoted: { label: 'Promoted', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  spam:     { label: 'Spam',     cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  awaiting_address_correction: { label: 'Awaiting Address Fix', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
};

export default function WebLeadsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('new');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/web-leads${qs}`);
      setRows(res.data.rows || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load web leads');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/web-leads/${id}`, { status });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (id) => {
    if (!window.confirm('Convert this web lead into a CRM client + appraisal?')) return;
    setBusyId(id);
    try {
      const res = await api.post(`/web-leads/${id}/convert`);
      const appraisalId = res.data.appraisal_id;
      if (appraisalId) navigate(`/appraisals/${appraisalId}`);
      else await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Convert failed');
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="web-leads-page">
      <div className="flex items-center gap-3 mb-6">
        <Globe className="w-7 h-7 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-semibold">Website Enquiries</h1>
          <p className="text-sm text-slate-500">Contact-form submissions from roofstoremedy.co.nz</p>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="status-chips">
        {['new', 'reviewed', 'promoted', 'spam'].map((s) => {
          const meta = STATUS_META[s];
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full border text-sm font-medium transition ${
                active
                  ? meta.cls + ' ring-2 ring-offset-1 ring-current'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {meta.label} <span className="opacity-70">· {counts[s] || 0}</span>
            </button>
          );
        })}
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 rounded-full border text-sm font-medium transition ${
            statusFilter === ''
              ? 'bg-slate-800 text-white border-slate-800'
              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
          }`}
          data-testid="filter-all"
        >
          All
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="error-banner">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500" data-testid="loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center bg-slate-50 rounded-lg border border-slate-200" data-testid="empty-state">
          <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No {statusFilter || 'website'} enquiries yet</p>
          <p className="text-sm text-slate-500 mt-1">New leads from the website will land here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const meta = STATUS_META[row.status] || STATUS_META.new;
            return (
              <div
                key={row.id}
                data-testid={`lead-card-${row.id}`}
                className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <h3 className="font-semibold text-slate-900 truncate">{row.name || '(no name)'}</h3>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtDate(row.received_at)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1 gap-x-6 text-sm text-slate-700 mb-2">
                      {row.email && (
                        <div className="flex items-center gap-1.5" data-testid="lead-email">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${row.email}`} className="text-sky-600 hover:underline">{row.email}</a>
                        </div>
                      )}
                      {row.phone && (
                        <div className="flex items-center gap-1.5" data-testid="lead-phone">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <a href={`tel:${row.phone}`} className="text-sky-600 hover:underline">{row.phone}</a>
                        </div>
                      )}
                      {row.address && (
                        <div className="flex items-center gap-1.5 md:col-span-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{row.address}</span>
                        </div>
                      )}
                      {row.source && (
                        <div className="flex items-center gap-1.5 md:col-span-2 text-xs text-slate-500">
                          <ExternalLink className="w-3 h-3" />
                          <span>{row.source}</span>
                        </div>
                      )}
                    </div>

                    {row.message && (
                      <div className="mt-2 p-3 bg-slate-50 rounded border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap" data-testid="lead-message">
                        {row.message}
                      </div>
                    )}

                    {/* Rich meta context — surfaced from website's `meta` shape */}
                    {(row.roof_type || row.roof_type_raw || row.roof_age || row.roof_stories || row.property_type || row.preferred_quote_types?.length > 0 || row.how_soon || row.preferred_contact_method || row.quality_discount_eligible || row.lead_no) && (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-600" data-testid="lead-meta">
                        {row.lead_no && (
                          <div className="flex items-center gap-1"><Hash className="w-3 h-3 text-slate-400" />Website #{row.lead_no}</div>
                        )}
                        {(row.roof_type || row.roof_type_raw) && (
                          <div className="flex items-center gap-1"><Home className="w-3 h-3 text-slate-400" />Roof: {row.roof_type || '—'}
                            {row.roof_type_needs_review && (
                              <span data-testid={`web-lead-roof-review-${row.id}`} className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="The website sent a roof type that isn't one of the 7 — confirm with the client">
                                Check: “{row.roof_type_raw}”
                              </span>
                            )}
                          </div>
                        )}
                        {row.roof_age && (
                          <div className="flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" />Age: {row.roof_age}</div>
                        )}
                        {row.roof_stories && (
                          <div className="flex items-center gap-1"><Home className="w-3 h-3 text-slate-400" />Storeys: {row.roof_stories}</div>
                        )}
                        {row.property_type && (
                          <div className="flex items-center gap-1"><Home className="w-3 h-3 text-slate-400" />{row.property_type}</div>
                        )}
                        {row.how_soon && (
                          <div className="flex items-center gap-1"><Zap className="w-3 h-3 text-slate-400" />{row.how_soon}</div>
                        )}
                        {row.preferred_contact_method && (
                          <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />via {row.preferred_contact_method}</div>
                        )}
                        {row.preferred_quote_types?.length > 0 && (
                          <div className="flex items-center gap-1 col-span-2 md:col-span-3"><Tag className="w-3 h-3 text-slate-400" />Requested: {row.preferred_quote_types.join(', ')}</div>
                        )}
                        {row.quality_discount_eligible && (
                          <div className="flex items-center gap-1 col-span-2 md:col-span-3 text-emerald-700 font-medium">
                            <Percent className="w-3 h-3" />5% quality discount eligible{row.discount_code ? ` · code: ${row.discount_code}` : ''}
                          </div>
                        )}
                      </div>
                    )}

                    {row.photos?.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <ImageIcon className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-500">{row.photos.length} photo{row.photos.length > 1 ? 's' : ''}</span>
                        {row.photos.slice(0, 4).map((p, i) => (
                          <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded border border-slate-200 overflow-hidden">
                            <img src={p.url} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}

                    {row.promoted_appraisal_id && (
                      <div className="mt-2 text-xs text-violet-700 flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" />
                        Converted to appraisal
                        <button
                          onClick={() => navigate(`/appraisals/${row.promoted_appraisal_id}`)}
                          className="ml-1 underline hover:no-underline"
                          data-testid={`open-appraisal-${row.id}`}
                        >
                          Open appraisal
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 shrink-0 min-w-[140px]">
                    {row.status === 'new' && (
                      <>
                        <button
                          onClick={() => navigate(`/free-appraisal/${row.id}`)}
                          data-testid={`free-appraisal-btn-${row.id}`}
                          className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 flex items-center justify-center gap-1"
                          title="Open the Free Appraisal form pre-filled with this lead's details"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Free Appraisal
                        </button>
                        <button
                          onClick={() => convert(row.id)}
                          disabled={busyId === row.id}
                          data-testid={`convert-btn-${row.id}`}
                          className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          title="Quick promote without opening the form"
                        >
                          Quick convert
                        </button>
                        <button
                          onClick={() => setStatus(row.id, 'reviewed')}
                          disabled={busyId === row.id}
                          data-testid={`review-btn-${row.id}`}
                          className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Mark reviewed
                        </button>
                        <button
                          onClick={() => setStatus(row.id, 'spam')}
                          disabled={busyId === row.id}
                          data-testid={`spam-btn-${row.id}`}
                          className="px-3 py-1.5 rounded border border-rose-200 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Spam
                        </button>
                      </>
                    )}
                    {row.status === 'reviewed' && (
                      <>
                        <button
                          onClick={() => navigate(`/free-appraisal/${row.id}`)}
                          data-testid={`free-appraisal-btn-${row.id}`}
                          className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 flex items-center justify-center gap-1"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Free Appraisal
                        </button>
                        <button
                          onClick={() => convert(row.id)}
                          disabled={busyId === row.id}
                          data-testid={`convert-btn-${row.id}`}
                          className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Quick convert
                        </button>
                        <button
                          onClick={() => setStatus(row.id, 'new')}
                          disabled={busyId === row.id}
                          className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Back to new
                        </button>
                      </>
                    )}
                    {row.status === 'spam' && (
                      <button
                        onClick={() => setStatus(row.id, 'new')}
                        disabled={busyId === row.id}
                        className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Not spam
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
